import { useState } from 'react';

/**
 * A compact tab strip for panels that would otherwise stack.
 *
 * The study page put the coach, the move list and the whole theory writeup in
 * one column, which made the column several times taller than the board beside
 * it — so most of it was below the fold and you had to scroll past the board to
 * reach anything. Tabs trade a click for not scrolling, which is the right way
 * round for reference material you dip into rather than read straight through.
 *
 * The panel body scrolls internally rather than growing, so the column stays
 * the height of the board no matter how long an opening's theory is.
 */
export interface Tab {
  key: string;
  label: string;
  /** A small count or marker beside the label. */
  badge?: React.ReactNode;
  content: React.ReactNode;
}

export default function Tabs({
  tabs,
  initial,
  className = '',
}: {
  tabs: Tab[];
  initial?: string;
  className?: string;
}) {
  const available = tabs.filter(Boolean);
  const [active, setActive] = useState(initial ?? available[0]?.key);
  // Falls back rather than rendering nothing if the active tab disappears —
  // the theory tab is absent until the opening has loaded.
  const current = available.find((t) => t.key === active) ?? available[0];

  if (!current) return null;

  return (
    <div className={`tabs ${className}`}>
      <div className="tabs-strip" role="tablist">
        {available.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === current.key}
            className={`tabs-tab${t.key === current.key ? ' tabs-tab--active' : ''}`}
            onClick={() => setActive(t.key)}
          >
            {t.label}
            {t.badge != null && <span className="tabs-badge">{t.badge}</span>}
          </button>
        ))}
      </div>
      <div className="tabs-panel" role="tabpanel">
        {current.content}
      </div>
    </div>
  );
}
